<template>
	<div>
		<div class="stage">
			<page-header title="Zones" />

			<filter-bar
				:show-filters="showFilters"
				:has-export="true"
				@toggle-filters="showFilters = !showFilters"
				@reset-filters="resetSearchParams"
				@export="exportToCsv"
			>
				<template #additional-buttons>
					<li v-if="$global.hasUserRole(userRoles, 'tariffcodes:create')">
						<a class="jmiBtn orange withIcon" onclick="document.getElementById('csvFile').click()">
							<span class="fa-light fa-upload"></span>Import
						</a>
					</li>
					<form class="hidden" enctype="multipart/form-data">
						<input
							id="csvFile"
							style="position: relative; overflow: hidden; opacity: 0"
							type="file"
							accept=".csv"
							name="csvFile"
							@change="fileUpload"
						/>
					</form>
				</template>
			</filter-bar>

			<filter-section
				:show="showFilters"
				:filters="searchParams"
				:search-term="searchField"
				:secondary-search-term="numberSearch"
				show-search-filter
				show-secondary-search-filter
				search-label="Prefix Name"
				secondary-search-label="Search by Number"
				@update:filters="searchParams = $event"
				@update:search-term="searchField = $event"
				@update:secondary-search-term="numberSearch = $event"
			/>

			<data-view
				:view-mode="'table'"
				:columns="columns"
				:sort-column="sortColumn"
				:reverse="reverse"
				:loading="loading"
				:label="'Codes'"
				:total-items="totalCodes"
				:page-size="pageSize"
				:current-page="currentPage"
				:pages="computedPages"
				:page-options="[50, 100, 250]"
				@sort="setSort"
				@update:page-size="changePageSize"
				@page-change="changePage"
			>
				<template #table-view>
					<generic-table
						:items="codes"
						:columns="columns"
						item-type="codes"
						base-route="tariffcodes"
						:can-edit="false"
						:not-clickable="true"
						:view-mode="'table'"
						:empty-state-config="{
							icon: 'fa-light fa-map',
							title: 'No zones yet',
							description: 'Import zone data from a CSV file to get started.',
							action: $global.hasUserRole(userRoles, 'tariffcodes:create')
								? {
										label: 'Import Zones',
										icon: 'fa-light fa-upload',
										handler: () => document.getElementById('csvFile').click()
									}
								: null
						}"
					>
						<template #mobile-view="{ item }">
							<div class="flex w-full flex-col">
								<div class="text-md absolute right-4 top-4 block max-w-[160px]">
									<p class="text-right font-semibold uppercase text-gray-400">Prefix</p>
									<p class="truncate text-right">{{ item.prefix }}</p>
								</div>

								<div class="flex flex-col gap-2">
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Prefix Name:</p>
										<p>{{ item.prefix_name }}</p>
									</div>
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Zone Name:</p>
										<p>{{ item.zone_name }}</p>
									</div>
								</div>
							</div>
						</template>
					</generic-table>
				</template>
			</data-view>
		</div>
	</div>
</template>

<script>
import tariffCodesApi from '../scripts/tariffcodes/tariffcodes.api'
import axios from 'axios'
import moment from 'moment'
import debounce from 'lodash.debounce'

export default {
	inject: ['user', 'userRoles'],
	data() {
		return {
			columns: [
				{
					key: 'prefix',
					label: 'Prefix',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-4 col-lg-4'
				},
				{
					key: 'prefix_name',
					label: 'Prefix Name',
					sortable: true,
					class: 'col-xs-12 col-sm-5 col-md-4 col-lg-4'
				},
				{
					key: 'zone_name',
					label: 'Zone Name',
					sortable: true,
					class: 'col-xs-12 col-sm-5 col-md-4 col-lg-4'
				}
			],
			showFilters: true,
			searchParams: {},
			codes: [],
			totalCodes: 0,
			pageSize: 100,
			currentPage: 1,
			sortColumn: 'prefix',
			reverse: false,
			loading: false,
			searchField: '',
			numberSearch: '',
			debouncedWatch: null,
			debouncedNumberSearch: null
		}
	},

	computed: {
		computedPages() {
			const numPages = Math.ceil(this.totalCodes / this.pageSize) || 1
			return Array.from({ length: numPages }, (_, i) => i)
		}
	},

	watch: {
		searchField(newValue, oldValue) {
			this.debouncedWatch(newValue, oldValue)
		},

		numberSearch(newValue) {
			this.debouncedNumberSearch(newValue)
		},

		searchParams: {
			handler() {
				this.reloadCodes()
			},
			deep: true
		}
	},

	created() {
		this.debouncedWatch = debounce(this.reloadCodes, 1000)
		this.debouncedNumberSearch = debounce(this.searchByNumber, 1000)
		if (this.$route.query?.globalSearch) {
			this.reloadCodes(this.$route.query.globalSearch)
		} else {
			this.reloadCodes()
		}
	},

	beforeUnmount() {
		this.debouncedWatch.cancel()
		this.debouncedNumberSearch.cancel()
	},

	methods: {
		changePage(page) {
			this.currentPage = page
			this.reloadCodes()
		},

		changePageSize(size) {
			this.pageSize = size
			const numPages = Math.ceil(this.totalCodes / size) || 1
			this.currentPage = Math.min(this.currentPage, numPages)
			this.$nextTick(() => {
				this.reloadCodes()
			})
		},

		setSort(column, reverse) {
			if (this.sortColumn !== column) {
				this.reverse = false
			} else {
				this.reverse = reverse
			}
			this.sortColumn = column
			this.reloadCodes()
		},

		resetSearchParams() {
			this.searchParams = {}
			this.searchField = ''
			this.numberSearch = ''
		},

		getSearchParams() {
			const params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1),
				where: {},
				order: {
					[this.sortColumn]: this.reverse ? 'DESC' : 'ASC'
				}
			}

			if (this.searchField) {
				params.searchTerm = this.searchField
			}

			if (this.searchParams.dateAddedFrom) {
				params.where.createdAtFrom = `${moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')} 00:00:00`
				params.where.createdAtTo = this.searchParams.dateAddedTo
					? `${moment(this.searchParams.dateAddedTo).format('YYYY-MM-DD')} 23:59:59`
					: `${moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')} 23:59:59`
			}

			return params
		},

		async reloadCodes(globalSearch) {
			try {
				this.loading = true
				let params = this.getSearchParams()

				if (globalSearch) {
					params = { ...params, searchTerm: globalSearch }
				} else if (this.searchField) {
					params.searchTerm = this.searchField
				}

				const response = await tariffCodesApi.getAll(params)
				this.totalCodes = response.data.count
				this.codes = response.data.results

				const maxPages = Math.ceil(this.totalCodes / this.pageSize) || 1
				if (this.currentPage > maxPages) {
					this.currentPage = 1
					this.reloadCodes(globalSearch)
					return
				}
			} catch (err) {
				console.error(err)
			} finally {
				this.loading = false
			}
		},

		async exportToCsv() {
			try {
				const searchParams = { ...this.getSearchParams(), exportCsv: true }
				const response = await axios.get(`${window.dtServerUrl}/tariffcodes`, { params: searchParams })

				const blob = new Blob([response.data], { type: response.headers.get('content-type') })
				const link = document.createElement('a')
				link.href = window.URL.createObjectURL(blob)
				link.download = 'teleflow-tariffcodes.csv'
				link.click()
			} catch (error) {
				console.error(error)
			}
		},

		async fileUpload(event) {
			if (!event?.target?.files?.length) return

			try {
				this.loading = true
				const formData = new FormData(document.forms[0])
				await axios.post(`${window.dtServerUrl}/tariffcodes/upload`, formData)
				this.reloadCodes()
			} catch (error) {
				console.error(error)
			} finally {
				this.loading = false
				event.target.value = null
			}
		},

		async searchByNumber() {
			try {
				this.loading = true
				const response = await tariffCodesApi.getAll({
					limit: this.pageSize,
					skip: this.pageSize * (this.currentPage - 1),
					where: {
						testNumber: this.numberSearch
					},
					order: {
						[this.sortColumn]: this.reverse ? 'DESC' : 'ASC'
					}
				})
				this.totalCodes = response.data.count
				this.codes = response.data.results

				const maxPages = Math.ceil(this.totalCodes / this.pageSize) || 1
				if (this.currentPage > maxPages) {
					this.currentPage = 1
					this.searchByNumber()
					return
				}
			} catch (error) {
				console.error(error)
			} finally {
				this.loading = false
			}
		}
	}
}
</script>
